import { hasDiscovered } from '../app/state';
import type { HotspotBounds, SceneHotspot } from '../scenes/scene.types';

export type HotspotState = 'normal' | 'suggested' | 'discovered' | 'disabled';

function boundsFor(hotspot: SceneHotspot, viewport: number): HotspotBounds {
  const responsive = hotspot.responsive;
  if (viewport < 768 && responsive?.mobile) return responsive.mobile;
  if (viewport < 1024 && responsive?.tablet) return responsive.tablet;
  return { x: hotspot.x, y: hotspot.y, width: hotspot.width, height: hotspot.height };
}

export function applyBounds(element: HTMLElement, hotspot: SceneHotspot, viewport: number): void {
  const bounds = boundsFor(hotspot, viewport);
  element.style.left = `${bounds.x}%`;
  element.style.top = `${bounds.y}%`;
  element.style.width = `${bounds.width}%`;
  element.style.height = `${bounds.height}%`;
}

/**
 * Un hotspot est un vrai <button> : clavier, focus et lecteur d'écran gratuits.
 * Jamais de zone invisible sans libellé (cf. §6 et §19).
 */
export function createHotspotElement(
  hotspot: SceneHotspot,
  onActivate: (hotspot: SceneHotspot) => void,
): HTMLButtonElement {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'hotspot';
  button.dataset['hotspotId'] = hotspot.id;
  button.dataset['action'] = hotspot.action;
  button.setAttribute('aria-label', hotspot.label);

  if (hotspot.zIndex !== undefined) button.style.zIndex = String(hotspot.zIndex);
  applyBounds(button, hotspot, window.innerWidth);

  if (hotspot.enabled === false) {
    button.disabled = true;
    button.dataset['state'] = 'disabled';
  } else {
    button.dataset['state'] = hasDiscovered(hotspot.id) ? 'discovered' : 'normal';
  }

  if (hotspot.hint) {
    const hint = document.createElement('span');
    hint.className = 'hotspot__hint';
    hint.textContent = hotspot.hint;
    hint.setAttribute('aria-hidden', 'true');
    button.append(hint);
  }

  button.addEventListener('click', () => {
    if (button.disabled) return;
    onActivate(hotspot);
  });

  return button;
}

export function setHotspotState(element: HTMLElement, state: HotspotState): void {
  if (state !== 'disabled' && element.dataset['state'] === 'disabled') return;
  element.dataset['state'] = state;
}

/** « Montrer les éléments interactifs » : révélation brève, non permanente. */
export function revealHotspots(container: HTMLElement, durationMs = 1800): void {
  container.classList.add('is-revealing');
  window.setTimeout(() => container.classList.remove('is-revealing'), durationMs);
}
